import { useRef, useState, useEffect, useMemo, type FocusEvent } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	FormControlLabel,
	Grid,
	Switch,
	TextField,
	Tooltip,
	Typography,
	Paper,
	LinearProgress,
	Stack,
	Divider,
	alpha,
	useTheme,
} from "@mui/material";
import {
	Close as CloseIcon,
	ConfirmationNumber,
	Payments as PaymentsIcon,
	AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import { createFilterOptions, Util } from "../../util/Util";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { useAppSocket } from "../../providers/AppSocketContext";
import RefreshButton from "../../components/RefreshButton";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { excludeExpensePayModes, isChangeTenderMode, isVoucherPayMode, isWalletPayMode, MYSQL_LIMITS, parseReceivedInput, paymentChangeAmount, receivedBelowAmount, seedPaymentReceived } from "../../util/constants";
import { MONEY_LIMITS, roundMoney, addMoney } from "../../util/quantity";
import {
	filterPayModesForWallet,
	isUsableWalletTender,
	useWalletTender,
	walletTenderCap,
} from "../wallet/useWalletTender";
import { Z_INDEX } from "../../styles/zIndex";
import VoucherTenderModal from "../voucher/VoucherTenderModal";
import {
	expandPaymentEntries,
	voucherModeId,
	voucherTenderTotal,
	type VoucherTenderLine,
} from "../voucher/voucherTender";
import { businessTodayDate, parseISODay } from "../../util/businessClock";
import { offerReceipt } from "../../util/receiptPrompt";

interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	editMode?: boolean;
	onSuccess?: () => void;
}

interface PaymentMode {
	id: string;
	mode_name: string;
	is_active: boolean;
}

export interface SelectedPayments {
	[key: string]: {
		enabled: boolean;
		amount: number;
		received?: number;
		vouchers?: import('../voucher/voucherTender').VoucherTenderLine[];
	};
}

const PaymentModal = ({ isOpen, toggle, bundle, editMode, onSuccess }: Props) => {
	const [selectedPayments, setSelectedPayments] = useState<SelectedPayments>({});
	const [selectedClient, setSelectedClient] = useState<any>(null);
	const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
	const [selectedDate, setSelectedDate] = useState<Date>(() => businessTodayDate());
	const [payDetails, setPayDetails] = useState<string>("");
	const [isInvoiceLoading, setIsInvoiceLoading] = useState<boolean>(false);
	const [isClientLoading, setIsClientLoading] = useState<boolean>(false);
	const [isPaymentOptionsLoading, setIsPaymentOptionsLoading] = useState<boolean>(false);

	const errorMessageRef = useRef<string>("");

	const [situations, setSituations] = useState<any[]>([]);
	const [clients, setClients] = useState<any[]>([]);
	const [payModes, setPayModes] = useState<PaymentMode[]>([]);
	const [selectedBalance, setSelectedBalance] = useState<number>(0);
	const { appSettings } = useAppSocket();
	const theme = useTheme();
	const themeColor = appSettings?.UI_THEME_PRIMARY?.value || theme.palette.primary.main;
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";

	const [errorInput, setErrorInput] = useState("");
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [voucherModalOpen, setVoucherModalOpen] = useState(false);
	const [issueChange, setIssueChange] = useState(false);

	const getSafePositiveAmount = (amount: number | undefined) =>
		Number.isFinite(amount) && (amount as number) > 0 ? (amount as number) : 0;

	const totalEnteredAmount = Object.values(selectedPayments)
		.filter((payment) => payment.enabled)
		.reduce((sum, payment) => addMoney(sum, getSafePositiveAmount(payment.amount)), 0);

	const canPay = Math.max(0, roundMoney(selectedBalance - totalEnteredAmount));
	const remainingBalance = canPay;
	const paymentProgress = selectedBalance > 0
		? Math.min(100, (totalEnteredAmount / selectedBalance) * 100)
		: 0;

	const { api, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { wallet: clientWallet, loading: walletLoading } = useWalletTender({
		clientId: selectedClient?.id,
		clientName: selectedClient?.acc_name,
		enabled: Boolean(selectedClient?.id || selectedClient?.acc_name),
	});
	const showWallet = isUsableWalletTender(clientWallet);
	const voucherId = voucherModeId(payModes);
	const appliedVouchers = selectedPayments[voucherId]?.vouchers || [];
	const voucherTotal = voucherTenderTotal(appliedVouchers);
	const isReturnInvoice = Boolean(
		selectedInvoice
		&& (
			Util.compareStrings(String(selectedInvoice.inv_type || ""), "Return")
			|| String(selectedInvoice.batch || "").startsWith("ret:")
		),
	);
	const showVoucher = !isReturnInvoice && Boolean(selectedPayments[voucherId]?.enabled && voucherTotal > 0);
	const visibleModes = useMemo(() => {
		if (isReturnInvoice) return excludeExpensePayModes(payModes);
		const modes = filterPayModesForWallet(payModes, showWallet);
		const voucherMode = payModes.find((mode) => isVoucherPayMode(mode.id));
		if (showVoucher && voucherMode && !modes.some((mode) => isVoucherPayMode(mode.id))) {
			return [...modes, voucherMode];
		}
		return modes;
	}, [payModes, showWallet, showVoucher, isReturnInvoice]);
	const editingWalletAmount =
		editMode && bundle && Util.compareStrings(String(bundle.mode || ''), 'Wallet')
			? Number(bundle.amount || 0)
			: 0;
	const walletCap = addMoney(walletTenderCap(clientWallet), Math.max(0, editingWalletAmount));

	const minDate = selectedInvoice?.date ? (parseISODay(selectedInvoice.date) as Date) : businessTodayDate();
	const canValidate = selectedDate && totalEnteredAmount > 0 && totalEnteredAmount <= selectedBalance;

	const formatMoney = (value: number) => Util.formatCurrency(value, currency);

	const invoiceLabel = (invoice: any) => {
		if (!invoice) return "";
		const dateLabel = invoice.date
			? Util.formatDate(invoice.date, "medium")
			: "";
		return `${dateLabel} · ${formatMoney(invoice.amount)} · ${invoice.reference || invoice.batch || ""}`;
	};

	const fetchPaymentOptions = async () => {
		setIsPaymentOptionsLoading(true);

		const res = await api.get("/payment/modes/active");

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.mode_name.localeCompare(b.mode_name));
				setPayModes(res.data);

				const initialPayments: SelectedPayments = {};
				res.data.forEach((mode: any) => {
					if (bundle && bundle?.mode && Util.compareStrings(bundle.mode, mode.mode_name)) {
						initialPayments[mode.id] = { enabled: true, amount: bundle.amount };
					} else {
						initialPayments[mode.id] = { enabled: false, amount: 0 };
					}
				});

				setSelectedPayments(initialPayments);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchPaymentOptions);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsPaymentOptionsLoading(false);
	};

	const fetchInvoices = async () => {
		if (!selectedClient?.acc_name) return;
		setIsInvoiceLoading(true);

		const res = await api.get(
			`/invoices/situations/clients/${selectedClient.acc_name}/${bundle?.batch ?? ""}`,
		);

		if (res) {
			if (res.status === 200) {
				res.data.sort(
					(a: any, b: any) =>
						new Date(b.date || b.Date).getTime() - new Date(a.date || a.Date).getTime(),
				);

				const normalized = res.data.map((invoice: any) => ({
					...invoice,
					amount: Math.abs(invoice.amount),
					balance: Math.abs(invoice.balance),
				}));

				setSituations(normalized);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchInvoices);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsInvoiceLoading(false);
	};

	const fetchClients = async () => {
		setIsClientLoading(true);

		const res = await api.get("/clients", { params: { Type: "CONSUMER" } });

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
				setClients(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchClients);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsClientLoading(false);
	};

	useEffect(() => {
		if (!isOpen) return;

		if (bundle?.client) {
			setSelectedClient({ acc_name: bundle.client });
		} else if (!bundle) {
			fetchClients();
		}

		fetchPaymentOptions();
		setSelectedDate(new Date());
		setPayDetails(editMode ? bundle?.description || "" : "");
	}, [isOpen]);

	useEffect(() => {
		if (situations.length > 0) {
			const match = bundle?.batch
				? situations.find((s) => s.batch === bundle.batch) ?? situations[0]
				: situations[0];
			setSelectedInvoice(match);
		} else {
			setSelectedInvoice(null);
		}
	}, [situations, bundle?.batch]);

	useEffect(() => {
		if (selectedClient) {
			fetchInvoices();
		} else {
			setSituations([]);
			setSelectedInvoice(null);
		}
	}, [selectedClient?.acc_name]);

	useEffect(() => {
		if (walletLoading || showWallet) return;
		setSelectedPayments((prev) => {
			let changed = false;
			const next = { ...prev };
			Object.keys(next).forEach((id) => {
				if (isWalletPayMode(id) && next[id]?.enabled) {
					next[id] = { ...next[id], enabled: false, amount: 0 };
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [showWallet, selectedClient?.acc_name, selectedClient?.id]);

	useEffect(() => {
		if (selectedInvoice) {
			if (editMode && bundle) {
				setSelectedBalance(Number(selectedInvoice.balance) + Number(bundle.amount));
			} else {
				setSelectedBalance(Number(selectedInvoice.balance) || 0);
			}
		} else {
			setSelectedBalance(0);
		}
	}, [selectedInvoice, editMode, bundle]);

	const applyVouchers = (lines: VoucherTenderLine[]) => {
		const total = voucherTenderTotal(lines);
		setSelectedPayments((prev) => ({
			...prev,
			[voucherId]: {
				enabled: total > 0,
				amount: total,
				vouchers: total > 0 ? lines : [],
			},
		}));
	};

	const toggleIssueChange = (next: boolean) => {
		setIssueChange(next);
		setSelectedPayments((prev) => {
			const updated = { ...prev };
			Object.keys(updated).forEach((id) => {
				if (!updated[id]) return;
				if (!next) {
					updated[id] = { ...updated[id], received: 0 };
					return;
				}
				if (
					updated[id].enabled
					&& isChangeTenderMode(id)
					&& getSafePositiveAmount(updated[id].amount) > 0
				) {
					updated[id] = { ...updated[id], received: seedPaymentReceived(updated[id]) };
				}
			});
			return updated;
		});
	};

	useEffect(() => {
		if (!isReturnInvoice) return;
		if (issueChange) toggleIssueChange(false);
		if (voucherTotal > 0) applyVouchers([]);
	}, [isReturnInvoice]);

	const onToggleMode = (id: string) => {
		if (isVoucherPayMode(id)) return;
		setSelectedPayments((prev) => {
			const current = prev[id] || { enabled: false, amount: 0 };
			const openAmount = Math.max(0, addMoney(remainingBalance, getSafePositiveAmount(current.amount)));
			const nextAmount = isWalletPayMode(id)
				? Math.min(openAmount, walletCap)
				: openAmount;
			return {
				...prev,
				[id]: {
					...current,
					enabled: !current.enabled,
					amount: current.enabled ? 0 : nextAmount,
					received: current.enabled
						? 0
						: issueChange && isChangeTenderMode(id)
							? nextAmount
							: current.received || 0,
				},
			};
		});
	};

	const onAmountChange = (id: string, newAmount: number) => {
		if (isVoucherPayMode(id)) return;
		if (!Number.isFinite(newAmount)) {
			setSelectedPayments((prev) => ({
				...prev,
				[id]: { ...prev[id], amount: 0 },
			}));
			return;
		}

		newAmount = roundMoney(newAmount);
		if (newAmount < 0.01 || newAmount > MONEY_LIMITS.max) {
			setErrorInput(id);
			errorMessageRef.current = `Amount must be between 0.01 and ${Util.formatNumber(MONEY_LIMITS.max)}!`;
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = "";
			}, 2000);
			return;
		}

		const newTotal = roundMoney(Object.values(selectedPayments)
				.filter((payment) => payment.enabled)
				.reduce((sum, payment) => addMoney(sum, getSafePositiveAmount(payment.amount)), 0) -
			getSafePositiveAmount(selectedPayments[id]?.amount) +
			newAmount);

		if (isWalletPayMode(id) && newAmount > walletCap) {
			setErrorInput(id);
			errorMessageRef.current = t("common:wallet.tenderExceeds", { max: Util.formatNumber(walletCap) });
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = "";
			}, 3000);
			return;
		}

		if (newTotal > selectedBalance) {
			setErrorInput(id);
			errorMessageRef.current = `Amount to pay exceeds invoice balance of: ${Util.formatNumber(selectedBalance)}!`;
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = "";
			}, 3000);
			return;
		}

		const currentReceived = Math.max(0, Number(selectedPayments[id]?.received || 0));
		setSelectedPayments((prev) => ({
			...prev,
			[id]: {
				...prev[id],
				amount: newAmount,
				received: issueChange && isChangeTenderMode(id)
					? seedPaymentReceived({ amount: newAmount, received: currentReceived })
					: currentReceived > 0 ? Math.max(currentReceived, newAmount) : 0,
			},
		}));
	};

	const payFullBalance = () => {
		if (!selectedInvoice || selectedBalance <= 0 || visibleModes.length === 0) return;
		const targetId = visibleModes[0].id;
		const next: SelectedPayments = {};
		visibleModes.forEach((mode) => {
			if (isVoucherPayMode(mode.id)) {
				next[mode.id] = selectedPayments[mode.id] || { enabled: false, amount: 0, vouchers: [] };
				return;
			}
			const full = isWalletPayMode(mode.id)
				? Math.min(selectedBalance, walletCap)
				: selectedBalance;
			next[mode.id] = {
				enabled: mode.id === targetId,
				amount: mode.id === targetId ? full : 0,
				received: mode.id === targetId && issueChange && isChangeTenderMode(mode.id) ? full : 0,
			};
		});
		if (selectedPayments[voucherId]) next[voucherId] = selectedPayments[voucherId];
		setSelectedPayments(next);
	};

	const handlePayment = async () => {
		if (!Util.checkDating(Util.formatISODate(selectedDate), access, 'payment')) {
			return;
		}

		const payload = expandPaymentEntries(
			Object.entries(selectedPayments),
			{
				invoice: selectedInvoice.batch,
				pay_date: Util.formatISODate(selectedDate),
				is_instant:
					Util.formatISODate(selectedDate) ===
					String(selectedInvoice?.date ?? "").slice(0, 10),
				description: payDetails,
			},
			{ includeChange: issueChange },
		);

		setLoadingMessage(editMode ? "UPDATE" : "SAVE");

		const res = !editMode
			? await api.post("/payments", payload)
			: await api.put(`/payments/${bundle.id}`, payload);

		if (res) {
			if (res.status === 201 || res.status === 204) {
				const savedIds: number[] = Array.isArray(res.data?.ids) ? res.data.ids : [];
				if (!editMode && !isReturnInvoice && savedIds.length > 0) {
					offerReceipt({ source: { kind: "payments", ids: savedIds }, message: t("common:receipt.afterPayment") });
				}
				if (bundle) {
					toggle();
					onSuccess?.();
				} else {
					fetchInvoices();
					toast.success(t("common:toast.success.payment"), { autoClose: 3000 });
				}
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.deposited"), { autoClose: 3000 });
			} else {
				console.error("Failed to send data:", res);
			}
		}

		setLoadingMessage(null);
	};

	const selectOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		event.target.select();
	};

	const focusReceived = (id: string) => (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const tender = getSafePositiveAmount(selectedPayments[id]?.amount);
		const current = Math.max(0, Number(selectedPayments[id]?.received || 0));
		if (current <= 0 && tender > 0) {
			setSelectedPayments((prev) => ({
				...prev,
				[id]: { ...prev[id], received: tender },
			}));
		}
		requestAnimationFrame(() => event.target.select());
	};

	const summaryItems = useMemo(
		() =>
			selectedInvoice
				? [
						{ label: t("common:payments.modal.invoiceDate"), value: Util.formatDate(selectedInvoice.date, "medium") },
						{ label: t("common:tables.amount"), value: formatMoney(selectedInvoice.amount) },
						{ label: t("common:payments.modal.paid"), value: formatMoney(selectedInvoice.paid ?? 0) },
						{ label: t("common:tables.owing"), value: formatMoney(selectedInvoice.balance) },
						{ label: t("common:tables.reference"), value: selectedInvoice.reference || "—" },
						{ label: t("common:report.invoiceBy"), value: selectedInvoice.user || "—" },
					]
				: [],
		[selectedInvoice, t, currency],
	);

	const panelSx = {
		p: 2,
		borderRadius: 2.5,
		border: "1px solid",
		borderColor: "divider",
		boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
		height: "100%",
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={toggle}
				maxWidth="lg"
				fullWidth
				sx={{ zIndex: Z_INDEX.dialog }}
				disableEscapeKeyDown
				PaperProps={{
					sx: {
						borderRadius: 3,
						borderTop: `3px solid ${themeColor}`,
						overflow: "hidden",
					},
				}}
			>
				<DialogTitle
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 2,
						pb: 1,
						background: `linear-gradient(180deg, ${alpha(themeColor, 0.07)} 0%, transparent 100%)`,
					}}
				>
					<Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								bgcolor: alpha(themeColor, 0.12),
								color: themeColor,
								flexShrink: 0,
							}}
						>
							<PaymentsIcon />
						</Box>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="h6" fontWeight={700} color={themeColor} noWrap>
								{t("common:payments.modal.title")}
							</Typography>
							<Typography variant="body2" color="text.secondary" noWrap>
								{selectedClient?.acc_name || t("common:payments.modal.selectClient")}
							</Typography>
						</Box>
					</Stack>
					<IconButton onClick={toggle} aria-label={t("common:ui.buttons.cancel")} sx={{ mt: -0.5 }}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ pt: 1 }}>
					{selectedInvoice && (
						<Paper
							elevation={0}
							sx={{
								...panelSx,
								mb: 2,
								bgcolor: alpha(themeColor, 0.03),
								borderColor: alpha(themeColor, 0.2),
							}}
						>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								justifyContent="space-between"
								alignItems={{ xs: "flex-start", sm: "center" }}
								spacing={1}
								sx={{ mb: 1.5 }}
							>
								<Box>
									<Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
										{t("common:report.invoiceDetails")}
									</Typography>
									<Typography variant="subtitle1" fontWeight={700}>
										{selectedInvoice.reference || selectedInvoice.batch}
									</Typography>
								</Box>
								<Button
									size="small"
									variant="outlined"
									onClick={payFullBalance}
									disabled={!selectedInvoice || selectedBalance <= 0 || isPaymentOptionsLoading}
								>
									{t("common:payments.modal.payFullBalance")}
								</Button>
							</Stack>

							<Grid container spacing={1.5} sx={{ mb: 1.5 }}>
								{summaryItems.map((item) => (
									<Grid key={item.label} size={{ xs: 6, sm: 4, md: 2 }}>
										<Typography variant="caption" color="text.secondary" display="block">
											{item.label}
										</Typography>
										<Typography variant="body2" fontWeight={600} noWrap title={String(item.value)}>
											{item.value}
										</Typography>
									</Grid>
								))}
							</Grid>

							<Box>
								<Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
									<Typography variant="caption" color="text.secondary">
										{t("common:payments.modal.allocationProgress")}
									</Typography>
									<Typography variant="caption" fontWeight={700}>
										{formatMoney(totalEnteredAmount)} / {formatMoney(selectedBalance)}
									</Typography>
								</Stack>
								<LinearProgress
									variant="determinate"
									value={paymentProgress}
									sx={{
										height: 8,
										borderRadius: 999,
										bgcolor: alpha(themeColor, 0.12),
										"& .MuiLinearProgress-bar": {
											borderRadius: 999,
											bgcolor: totalEnteredAmount > selectedBalance ? "error.main" : themeColor,
										},
									}}
								/>
							</Box>
						</Paper>
					)}

					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 7 }}>
							<Paper elevation={0} sx={panelSx}>
								<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
									{t("common:payments.modal.paymentDetails")}
								</Typography>

								<Stack spacing={2}>
									<Autocomplete
										id="clientInput"
										size="small"
										options={clients}
										filterOptions={createFilterOptions((client) => client.acc_name || "")}
										getOptionLabel={(client) => client.acc_name || ""}
										value={selectedClient}
										onChange={(_, newValue) => setSelectedClient(newValue)}
										loading={isClientLoading}
										autoHighlight
										disabled={isClientLoading || !!bundle}
										renderInput={(params) => (
											<TextField
												{...params}
												label={
													<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
														{t("common:ui.labels.client")}
														<RefreshButton
															isWorking={isClientLoading}
															onClick={fetchClients}
															disabled={isClientLoading || !!bundle}
														/>
													</Box>
												}
												variant="filled"
												fullWidth
											/>
										)}
									/>

									<Autocomplete
										id="invoiceInput"
										size="small"
										options={situations}
										filterOptions={createFilterOptions((invoice) => invoiceLabel(invoice))}
										getOptionLabel={(invoice) => invoiceLabel(invoice)}
										value={selectedInvoice}
										onChange={(_, newValue) => setSelectedInvoice(newValue)}
										loading={isInvoiceLoading}
										autoHighlight
										disabled={isInvoiceLoading || !selectedClient || !!bundle?.batch}
										renderInput={(params) => (
											<TextField
												{...params}
												label={
													<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
														{t("common:tables.invoiceNo")}
														<RefreshButton
															isWorking={isInvoiceLoading}
															onClick={fetchInvoices}
															disabled={isInvoiceLoading || !selectedClient}
														/>
													</Box>
												}
												variant="filled"
												fullWidth
												helperText={
													selectedInvoice
														? `${t("common:payments.modal.balanceToPay")}: ${formatMoney(addMoney(remainingBalance, totalEnteredAmount))} · ${t("common:payments.modal.remaining")}: ${formatMoney(remainingBalance)}`
														: t("common:payments.modal.selectInvoice")
												}
											/>
										)}
									/>

									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											label={
												<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
													{t("common:ui.labels.date")}
													<RefreshButton isWorking={false} onClick={() => setSelectedDate(new Date())} />
												</Box>
											}
											value={selectedDate}
											minDate={minDate}
											onChange={(newDate) => {
												if (
													!newDate ||
													Util.compareStrings(newDate.toString(), "Invalid Date") ||
													newDate < new Date(minDate)
												) {
													setErrorInput("dateInput");
													setTimeout(() => setErrorInput(""), 1500);
													return;
												}
												const date = newDate instanceof Date ? newDate : newDate.toDate();
												setSelectedDate(date);
											}}
											disabled={!selectedInvoice}
											slotProps={{
												textField: {
													id: "dateInput",
													size: "small",
													fullWidth: true,
													variant: "filled",
													error: errorInput === "dateInput",
													helperText:
														errorInput === "dateInput" ? t("common:validation.invalidDate") : "",
												},
											}}
										/>
									</LocalizationProvider>

									<TextField
										id="detailsInput"
										size="small"
										label={t("common:ui.labels.notes")}
										variant="filled"
										fullWidth
										multiline
										minRows={2}
										value={payDetails}
										inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
										onChange={(e) => setPayDetails(e.target.value)}
									/>
								</Stack>
							</Paper>
						</Grid>

						<Grid size={{ xs: 12, md: 5 }}>
							<Paper elevation={0} sx={panelSx}>
								<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
									<Typography variant="subtitle2" fontWeight={700}>
										{t("common:ui.labels.paymentOptions")}
									</Typography>
									<Stack direction="row" spacing={0.5} alignItems="center">
										{!isReturnInvoice && (
											<Tooltip title={t("common:voucher.tender.open")}>
												<IconButton color="primary" size="small" onClick={() => setVoucherModalOpen(true)}>
													<ConfirmationNumber fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
										<RefreshButton
											isWorking={isPaymentOptionsLoading}
											onClick={fetchPaymentOptions}
											disabled={isPaymentOptionsLoading}
										/>
									</Stack>
								</Stack>
								{!isReturnInvoice && (
									<FormControlLabel
										sx={{ mb: 1 }}
										control={
											<Switch
												size="small"
												checked={issueChange}
												onChange={(e) => toggleIssueChange(e.target.checked)}
											/>
										}
										label={t("common:voucher.issueChange")}
									/>
								)}

								<Stack spacing={1}>
									{visibleModes.map((mode) => {
										const enabled = selectedPayments[mode.id]?.enabled || false;
										const showReceived =
											issueChange
											&& isChangeTenderMode(mode.id)
											&& enabled
											&& Number(selectedPayments[mode.id]?.amount || 0) > 0;
										const amountField = (
											<TextField
												size="small"
												variant="filled"
												type="number"
												label={showReceived ? t("common:tables.amount") : undefined}
												value={
													Number.isFinite(selectedPayments[mode.id]?.amount)
														? selectedPayments[mode.id]?.amount
														: 0
												}
												required={enabled}
												disabled={!enabled || isPaymentOptionsLoading || isVoucherPayMode(mode.id)}
												onFocus={selectOnFocus}
												onClick={(e) => e.stopPropagation()}
												onChange={(e) => onAmountChange(mode.id, Number(e.target.value))}
												error={errorInput === mode.id}
												helperText={
													errorInput === mode.id
														? errorMessageRef.current
														: isWalletPayMode(mode.id) && clientWallet && !showReceived
															? t("common:wallet.tenderHint", { amount: Util.formatNumber(clientWallet.balance) })
															: " "
												}
												sx={showReceived ? { flex: 1, minWidth: 0 } : { width: { xs: 110, sm: 130 } }}
												slotProps={{
													htmlInput: {
														min: 0,
														step: 0.01,
														max: isWalletPayMode(mode.id) ? walletCap : undefined,
														style: { textAlign: "right" },
													},
												}}
											/>
										);
										return (
											<Box
												key={mode.id}
												onClick={() => !isPaymentOptionsLoading && !isVoucherPayMode(mode.id) && onToggleMode(mode.id)}
												sx={{
													display: "flex",
													flexDirection: showReceived ? "column" : "row",
													alignItems: showReceived ? "stretch" : "center",
													gap: 1,
													p: 1,
													borderRadius: 2,
													border: "1px solid",
													borderColor: enabled
														? alpha(themeColor, 0.45)
														: "divider",
													bgcolor: enabled ? alpha(themeColor, 0.06) : "transparent",
													cursor: isPaymentOptionsLoading ? "default" : "pointer",
													transition: "border-color 0.2s, background-color 0.2s",
													"&:hover": isPaymentOptionsLoading
														? undefined
														: { bgcolor: alpha(themeColor, 0.08) },
												}}
											>
												<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
													<Checkbox
														checked={enabled}
														onChange={() => onToggleMode(mode.id)}
														disabled={isPaymentOptionsLoading || isVoucherPayMode(mode.id)}
														onClick={(e) => e.stopPropagation()}
														sx={{ p: 0.5 }}
													/>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography variant="body2" fontWeight={600} noWrap>
															{mode.mode_name}
														</Typography>
														{isWalletPayMode(mode.id) && clientWallet && (
															<Typography variant="caption" color="text.secondary" noWrap>
																{t("common:wallet.tenderHint", { amount: Util.formatNumber(clientWallet.balance) })}
															</Typography>
														)}
													</Box>
													{!showReceived ? amountField : null}
												</Box>
												{showReceived ? (
													<Box
														sx={{ display: "flex", gap: 1, minWidth: 0 }}
														onClick={(e) => e.stopPropagation()}
													>
														{amountField}
														<TextField
															size="small"
															variant="filled"
															type="number"
															label={t("common:voucher.receivedAmount")}
															value={Number.isFinite(selectedPayments[mode.id]?.received) ? selectedPayments[mode.id]?.received : 0}
															onFocus={focusReceived(mode.id)}
															onChange={(e) => {
																const next = parseReceivedInput(e.target.value);
																setSelectedPayments((prev) => ({
																	...prev,
																	[mode.id]: { ...prev[mode.id], received: next },
																}));
															}}
															error={receivedBelowAmount(selectedPayments[mode.id])}
															sx={{ flex: 1, minWidth: 0 }}
															helperText={
																receivedBelowAmount(selectedPayments[mode.id])
																	? t("common:voucher.receivedBelowAmount", {
																		amount: Util.formatNumber(selectedPayments[mode.id]?.amount || 0),
																	})
																	: t("common:voucher.changeHint", { amount: Util.formatNumber(paymentChangeAmount(selectedPayments[mode.id])) })
															}
															slotProps={{
																htmlInput: {
																	min: 0,
																	step: 0.01,
																	style: { textAlign: "right" },
																},
															}}
														/>
													</Box>
												) : null}
											</Box>
										);
									})}
								</Stack>

								<Divider sx={{ my: 1.5 }} />

								<Stack spacing={1}>
									<Stack direction="row" justifyContent="space-between" alignItems="center">
										<Typography variant="body2" color="text.secondary">
											{t("common:ui.labels.totalToPay")}
										</Typography>
										<Chip
											icon={<WalletIcon sx={{ fontSize: "16px !important" }} />}
											label={formatMoney(totalEnteredAmount)}
											color={totalEnteredAmount > selectedBalance ? "error" : "success"}
											size="small"
											sx={{ fontWeight: 700 }}
										/>
									</Stack>
									<Stack direction="row" justifyContent="space-between" alignItems="center">
										<Typography variant="body2" color="text.secondary">
											{t("common:payments.modal.remaining")}
										</Typography>
										<Typography
											variant="body2"
											fontWeight={700}
											color={remainingBalance === 0 && totalEnteredAmount > 0 ? "success.main" : "text.primary"}
										>
											{formatMoney(remainingBalance)}
										</Typography>
									</Stack>
								</Stack>
							</Paper>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions
					sx={{
						px: 3,
						py: 2,
						borderTop: "1px solid",
						borderColor: "divider",
						bgcolor: alpha(theme.palette.background.default, 0.6),
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
						{selectedInvoice
							? `${t("common:payments.modal.confirmHint")} ${formatMoney(totalEnteredAmount)}`
							: t("common:payments.modal.selectInvoice")}
					</Typography>
					<Stack direction="row" spacing={1}>
						<Button color="inherit" variant="outlined" onClick={toggle}>
							{t("common:ui.buttons.cancel")}
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={() => setConfirmModalOpen(true)}
							disabled={!canValidate}
						>
							{t("common:ui.buttons.confirmPayment")}
						</Button>
					</Stack>
				</DialogActions>
			</Dialog>

			<VoucherTenderModal
				isOpen={voucherModalOpen}
				onClose={() => setVoucherModalOpen(false)}
				initialLines={appliedVouchers}
				maxSpend={Math.max(0, roundMoney(selectedBalance - totalEnteredAmount + voucherTotal))}
				onApply={applyVouchers}
			/>

			<ConfirmDialog
				isOpen={confirmModalOpen}
				setIsOpen={setConfirmModalOpen}
				title={t("common:dialog.paymentConfirmation")}
				confirmBtnColor="success"
				confirmBtnVariant="contained"
				onConfirm={handlePayment}
			/>
		</>
	);
};

export default PaymentModal;